/**
 * Firm Knowledge > Intake Templates: the firm's reusable questionnaires,
 * managed without opening a client. One table in three groups: Firm
 * Templates (visa, size, what every intake starts with, sends, owner),
 * CaseBlink Starters (read-only; Duplicate to Customize), and Archived (out
 * of every picker, restorable). Hover a row for its main action, or use its
 * menu; click it to open the editor, which takes the page (`?template=`).
 * Using one still picks a client, because an intake writes to a client's
 * record.
 */
import {
  Archive,
  ChevronDown,
  ChevronRight,
  Copy,
  FileText,
  Info,
  LayoutTemplate,
  Lock,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  RotateCcw,
  Send,
  X
} from "lucide-react";
import { useCallback, useMemo, useState, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeSavedTemplate
} from "@/api/types/outgoingIntake";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ListSearch } from "@/components/ui/list-toolbar";
import { Tag } from "@/components/ui/tag";
import { useTeamMembers } from "@/features/agents/useTeamMembers";
import {
  ClientTable,
  type ClientTableColumn
} from "@/features/clientProfile/components/table/ClientTable";
import { ClientTableRow } from "@/features/clientProfile/components/table/ClientTableRow";
import {
  ClientTableCell,
  MutedCell,
  TitleCell,
  initialsOf
} from "@/features/clientProfile/components/table/cells";
import { formatMdY } from "@/features/clientProfile/lib/format";
import { cn } from "@/lib/utils";

import type { FirmKnowledgeScope } from "../hooks/useFirmScope";
import type { IntakeTemplatesState } from "../hooks/useIntakeTemplates";
import {
  duplicateName,
  groupTemplates,
  sentLabel,
  type IntakeTemplateRow
} from "../intakeTemplates";
import { IntakeTemplateEditor } from "./IntakeTemplateEditor";
import { UseTemplateDialog, type UsableTemplate } from "./UseTemplateDialog";

type Props = {
  templates: IntakeTemplatesState;
  firm: FirmKnowledgeScope;
  /** The open template (`?template=`); the editor takes the page. */
  openTemplateId: string | null;
  onOpenTemplate: (templateId: string | null) => void;
  /** Firm Knowledge's first tab, the editor's first crumb. */
  onHome: () => void;
  /** The Library / Skills / Intake Templates segment, first in the toolbar. */
  toolbarLeading?: ReactNode;
  /** Ask Casey to revise a template, from its editor. */
  onAskCasey?: (template: OutgoingIntakeSavedTemplate) => void;
};

const COLUMNS: ClientTableColumn[] = [
  { id: "template", header: "Template", style: { width: "36%" } },
  { id: "visa", header: "Visa", style: { width: "9%" } },
  { id: "size", header: "Size", style: { width: "14%" } },
  { id: "starts", header: "Starts With", style: { width: "11%" } },
  { id: "used", header: "Used", style: { width: "12%" } },
  { id: "updated", header: "Updated", style: { width: "8%" } },
  { id: "owner", header: "Owner", style: { width: "6%" } },
  { id: "actions", header: "", style: { width: "4%" } }
];

const INFO_DISMISSED_KEY = "caseblink.intakeTemplates.infoDismissed";

const readDismissed = () => {
  try {
    return window.localStorage.getItem(INFO_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
};

/** What a template is for, once, until dismissed. */
const InfoStrip: FC = () => {
  const [dismissed, setDismissed] = useState(readDismissed);
  if (dismissed) return null;
  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(INFO_DISMISSED_KEY, "1");
    } catch {
      // Private mode: it stays dismissed for this visit.
    }
  };
  return (
    <div
      className="flex shrink-0 items-start gap-2 border-b border-line-1 bg-info-soft px-6 py-2 text-sm text-fg-2"
      data-testid="templates-info"
    >
      <Info className="mt-0.5 size-3.5 shrink-0 text-info" aria-hidden />
      <span className="min-w-0 flex-1">
        A template is the questions, note, video, link and reminders every new
        intake starts with. Use one for a client to make their intake; edit it
        here and the next intake gets the change, while sent ones keep theirs.
      </span>
      <Button
        variant="ghost"
        size="icon-2xs"
        aria-label="Dismiss"
        onClick={dismiss}
      >
        <X />
      </Button>
    </div>
  );
};

type GroupId = "firm" | "starters" | "archived";

const GROUP_LABEL: Record<GroupId, { label: string; note: string }> = {
  firm: { label: "Firm Templates", note: "your firm's own; edit freely" },
  starters: {
    label: "CaseBlink Starters",
    note: "kept current by CaseBlink; duplicate one to change it"
  },
  archived: {
    label: "Archived",
    note: "out of every picker; intakes built from them keep their questions"
  }
};

export const IntakeTemplatesTab: FC<Props> = ({
  templates,
  firm,
  openTemplateId,
  onOpenTemplate,
  onHome,
  toolbarLeading,
  onAskCasey
}) => {
  const canEdit = firm.canEditLibrary;
  const { members } = useTeamMembers();
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Set<GroupId>>(
    () => new Set<GroupId>(["archived"])
  );
  const [using, setUsing] = useState<{
    template: UsableTemplate;
    beforeSend?: () => Promise<void>;
  } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const groups = useMemo(
    () => groupTemplates(templates.saved, templates.catalog, query),
    [templates.saved, templates.catalog, query]
  );
  const ownerName = useCallback(
    (uid: string | null) =>
      (uid && members.find((m) => m.uid === uid)?.label) || null,
    [members]
  );

  const toggle = (id: GroupId) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openUse = async (
    row: Pick<IntakeTemplateRow, "id" | "name" | "kind">,
    known?: UsableTemplate,
    beforeSend?: () => Promise<void>
  ) => {
    const saved = row.kind === "firm" ? templates.savedById(row.id) : null;
    const template: UsableTemplate = known ?? {
      id: row.id,
      name: row.name,
      schema: saved ? saved.schema ?? [] : null,
      defaults: saved
    };
    setUsing({ template, beforeSend });
    if (template.schema) return;
    try {
      const loaded: OutgoingIntakeFormNode[] =
        await templates.loadStarterSchema(row.id);
      setUsing((current) =>
        current?.template.id === row.id
          ? { ...current, template: { ...current.template, schema: loaded } }
          : current
      );
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Couldn't load the template"
      );
    }
  };

  const duplicate = async (row: IntakeTemplateRow) => {
    setBusyId(row.id);
    try {
      const source = templates.savedById(row.id);
      const schema =
        source?.schema ?? (await templates.loadStarterSchema(row.id));
      const copy = await OutgoingIntakesAPI.createSavedTemplate({
        name: duplicateName(row),
        schema,
        visa: row.visa,
        description: row.description,
        ...(source
          ? {
              attorney_note: source.attorney_note ?? null,
              expires_in_days: source.expires_in_days ?? null,
              reminder_schedule: source.reminder_schedule ?? null,
              enable_firm_branding: source.enable_firm_branding ?? null
            }
          : {})
      });
      templates.applySaved(copy);
      toast.success(`Duplicated to Firm Templates as “${copy.name}”`, {
        action: {
          label: "Open",
          onClick: () => onOpenTemplate(copy.template_id)
        }
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't duplicate it");
    } finally {
      setBusyId(null);
    }
  };

  const setArchived = useCallback(
    async (template: OutgoingIntakeSavedTemplate, archived: boolean) => {
      try {
        const updated = await OutgoingIntakesAPI.updateSavedTemplate(
          template.template_id,
          { archived }
        );
        templates.applySaved(updated);
        toast.success(
          archived
            ? `Archived “${template.name}”. Intakes built from it keep their questions.`
            : `Restored “${template.name}”`,
          archived
            ? {
                action: {
                  label: "Undo",
                  onClick: () =>
                    void OutgoingIntakesAPI.updateSavedTemplate(
                      template.template_id,
                      { archived: false }
                    ).then(templates.applySaved)
                }
              }
            : undefined
        );
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Couldn't change it");
      }
    },
    [templates]
  );

  const useDialog = (
    <UseTemplateDialog
      template={using?.template ?? null}
      beforeSend={using?.beforeSend}
      onOpenChange={(open) => !open && setUsing(null)}
      onDone={() => void templates.refresh()}
    />
  );

  if (openTemplateId) {
    return (
      <>
        <IntakeTemplateEditor
          templateId={openTemplateId}
          templates={templates}
          canEdit={canEdit}
          onHome={onHome}
          onBack={() => onOpenTemplate(null)}
          onOpen={onOpenTemplate}
          onUse={(template, beforeSend) =>
            void openUse(
              {
                id: template.id,
                name: template.name,
                kind: templates.savedById(template.id) ? "firm" : "starter"
              },
              template,
              beforeSend
            )
          }
          onArchive={(template, archived) =>
            void setArchived(template, archived)
          }
          onAskCasey={onAskCasey}
        />
        {useDialog}
      </>
    );
  }

  const menuFor = (row: IntakeTemplateRow) => {
    const saved = templates.savedById(row.id);
    if (row.archived && saved)
      return (
        <>
          <DropdownMenuItem onSelect={() => onOpenTemplate(row.id)}>
            <FileText /> Open
          </DropdownMenuItem>
          {canEdit ? (
            <DropdownMenuItem onSelect={() => void setArchived(saved, false)}>
              <RotateCcw /> Restore
            </DropdownMenuItem>
          ) : null}
        </>
      );
    if (row.kind === "starter")
      return (
        <>
          <DropdownMenuItem onSelect={() => onOpenTemplate(row.id)}>
            <FileText /> Open
          </DropdownMenuItem>
          {canEdit ? (
            <DropdownMenuItem onSelect={() => void duplicate(row)}>
              <Copy /> Duplicate to Customize
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={() => void openUse(row)}>
            <Send /> Use for a Client…
          </DropdownMenuItem>
        </>
      );
    return (
      <>
        <DropdownMenuItem onSelect={() => onOpenTemplate(row.id)}>
          <Pencil /> {canEdit ? "Edit" : "Open"}
        </DropdownMenuItem>
        {canEdit ? (
          <DropdownMenuItem onSelect={() => void duplicate(row)}>
            <Copy /> Duplicate
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => void openUse(row)}>
          <Send /> Use for a Client…
        </DropdownMenuItem>
        {canEdit && saved ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void setArchived(saved, true)}>
              <Archive /> Archive
            </DropdownMenuItem>
          </>
        ) : null}
      </>
    );
  };

  const row = (r: IntakeTemplateRow) => {
    const owner = ownerName(r.ownerId);
    return (
      <ClientTableRow
        key={`${r.kind}-${r.id}`}
        data-testid={`template-row-${
          r.kind === "starter" ? "starter" : r.archived ? "archived" : "firm"
        }`}
        ariaLabel={r.name}
        onActivate={() => onOpenTemplate(r.id)}
        className={cn("group/row", r.archived && "opacity-60")}
      >
        <TitleCell
          leading={
            <span className="flex size-6 items-center justify-center rounded-md bg-fill-2 text-fg-3">
              {r.form ? (
                <FileText className="size-3.5" aria-hidden />
              ) : (
                <LayoutTemplate className="size-3.5" aria-hidden />
              )}
            </span>
          }
          title={
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate">{r.name}</span>
              {r.kind === "starter" ? (
                <Lock
                  className="size-3 shrink-0 text-fg-4"
                  aria-label="Read-only"
                />
              ) : null}
            </span>
          }
          subtitle={r.description ?? undefined}
          trailing={
            r.archived ? null : (
              <span
                className="invisible shrink-0 group-hover/row:visible"
                data-row-stop
              >
                {r.kind === "starter" ? (
                  canEdit ? (
                    <Button
                      size="xs"
                      variant="secondary"
                      disabled={busyId === r.id}
                      onClick={() => void duplicate(r)}
                    >
                      <Copy />
                      Duplicate to Customize
                    </Button>
                  ) : null
                ) : (
                  <Button
                    size="xs"
                    variant="secondary"
                    onClick={() => void openUse(r)}
                    data-testid="template-row-use"
                  >
                    <Send />
                    Use for a Client…
                  </Button>
                )}
              </span>
            )
          }
        />
        <ClientTableCell>
          {r.visa ? (
            <Tag mono={r.form}>{r.visa}</Tag>
          ) : (
            <span className="text-sm text-fg-3">Any</span>
          )}
        </ClientTableCell>
        <MutedCell className="tabular-nums">{r.size}</MutedCell>
        <MutedCell>{r.startsWith ?? "—"}</MutedCell>
        <ClientTableCell>
          {r.kind === "starter" ? (
            <span className="text-sm text-fg-3">—</span>
          ) : (
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm text-fg-2">
                {sentLabel(r.sent)}
              </span>
              {r.lastSentAt ? (
                <span className="truncate text-sm text-fg-3">
                  Last on {formatMdY(r.lastSentAt)}
                </span>
              ) : null}
            </span>
          )}
        </ClientTableCell>
        <MutedCell>
          {r.kind === "starter"
            ? "CaseBlink"
            : r.updatedAt
            ? formatMdY(r.updatedAt)
            : "—"}
        </MutedCell>
        <ClientTableCell>
          {r.kind === "starter" ? (
            <span className="text-sm text-fg-3">CB</span>
          ) : (
            <Avatar size="sm" title={owner ?? undefined}>
              <AvatarFallback>{owner ? initialsOf(owner) : "?"}</AvatarFallback>
            </Avatar>
          )}
        </ClientTableCell>
        <ClientTableCell className="justify-end px-1">
          <span data-row-stop onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`More for ${r.name}`}
                  data-testid="template-row-menu"
                >
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {menuFor(r)}
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
        </ClientTableCell>
      </ClientTableRow>
    );
  };

  const groupHeader = (id: GroupId, count: number) => {
    const open = !collapsed.has(id) || Boolean(query.trim());
    const Icon = open ? ChevronDown : ChevronRight;
    return (
      <ClientTableRow
        key={`group-${id}`}
        data-testid={`template-group-${id}`}
        ariaLabel={`${GROUP_LABEL[id].label}, ${count}`}
        onActivate={() => toggle(id)}
        className="bg-fill-1 hover:bg-fill-2"
      >
        <ClientTableCell colSpan={COLUMNS.length} className="h-8 gap-2">
          <Icon className="size-3.5 text-fg-3" aria-hidden />
          {id === "archived" ? (
            <Archive className="size-3.5 text-fg-3" aria-hidden />
          ) : null}
          <span className="text-sm font-medium text-fg-2">
            {GROUP_LABEL[id].label}
          </span>
          <span className="text-sm text-fg-4 tabular-nums">{count}</span>
          <span className="ml-1.5 truncate text-sm text-fg-4">
            {GROUP_LABEL[id].note}
          </span>
        </ClientTableCell>
      </ClientTableRow>
    );
  };

  const section = (id: GroupId, rows: IntakeTemplateRow[]) =>
    rows.length === 0 && (id !== "firm" || query.trim())
      ? []
      : [
          groupHeader(id, rows.length),
          ...(!collapsed.has(id) || query.trim() ? rows.map(row) : [])
        ];

  const total =
    groups.firm.length + groups.starters.length + groups.archived.length;

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid="intake-templates-tab"
    >
      <div className="flex w-full flex-none items-center gap-2 border-b border-line-2 bg-surface px-6 pb-3">
        {toolbarLeading}
        <ListSearch
          className="w-50 shrink"
          placeholder="Search templates…"
          aria-label="Search Templates"
          value={query}
          onValueChange={setQuery}
        />
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Refresh"
          onClick={() => void templates.refresh()}
        >
          <RefreshCw />
        </Button>
      </div>
      <InfoStrip />
      <ClientTable
        ariaLabel="Intake templates"
        className="min-h-0 flex-1"
        tableClassName="table-fixed"
        columns={COLUMNS}
        isLoading={templates.loading}
        isEmpty={query.trim() ? total === 0 : templates.loading && total === 0}
        error={templates.error}
        onRetry={() => void templates.refresh()}
        empty={{
          title: "Nothing Matches",
          message: "Try a different name, visa or description."
        }}
      >
        {section("firm", groups.firm)}
        {groups.firm.length === 0 && !query.trim() && !templates.loading ? (
          <ClientTableRow key="firm-empty">
            <ClientTableCell
              colSpan={COLUMNS.length}
              className="text-sm text-fg-3"
            >
              No firm templates yet. Duplicate a starter below, or start one
              with New Template.
            </ClientTableCell>
          </ClientTableRow>
        ) : null}
        {section("starters", groups.starters)}
        {section("archived", groups.archived)}
      </ClientTable>
      {useDialog}
    </div>
  );
};
